// Pure reconnect timer policy for chat streams. The owning hook supplies the current refs and reopen action;
// this module owns only capped backoff and stale-generation cancellation.

interface MutableCell<T> {
  current: T;
}

export interface ChatStreamRetryRefs {
  readonly streamRetryRef: MutableCell<ReturnType<typeof setTimeout> | null>;
  readonly streamGenRef: MutableCell<number>;
  readonly mountedRef: MutableCell<boolean>;
}

const STREAM_RETRY_BASE_MS = 50;
const STREAM_RETRY_MAX_MS = 1_000;

/** How many reopen attempts one run's preview may consume before recovery gives up on the stream
 *  (chat-stream-lifecycle.ts clears to the empty preview): the delay was capped but the attempts were
 *  NOT, so a stream that kept dying against a record that stayed `running` reopened forever and pinned
 *  the composer in its thinking state. After exhaustion the 5s wake poll keeps following the run. */
export const STREAM_RETRY_MAX_ATTEMPTS = 8;

export function clearStreamRetry(refs: Pick<ChatStreamRetryRefs, 'streamRetryRef'>): void {
  if (refs.streamRetryRef.current !== null) clearTimeout(refs.streamRetryRef.current);
  refs.streamRetryRef.current = null;
}

export function scheduleStreamRetry(
  refs: ChatStreamRetryRefs,
  generation: number,
  attempt: number,
  reopen: () => void,
): void {
  clearStreamRetry(refs);
  const exponent = Math.min(Math.max(0, attempt - 1), 6);
  const delay = Math.min(STREAM_RETRY_BASE_MS * 2 ** exponent, STREAM_RETRY_MAX_MS);
  refs.streamRetryRef.current = setTimeout(() => {
    refs.streamRetryRef.current = null;
    if (refs.streamGenRef.current !== generation || !refs.mountedRef.current) return;
    reopen();
  }, delay);
}
